import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import LegalLayout from "@/components/LegalLayout";
import ContactFormLink from "@/components/ContactFormLink";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { formatApiError } from "@/lib/utils-app";
import { toast } from "sonner";
import { EnvelopeSimpleIcon as EnvelopeSimple, BuildingsIcon as Buildings, ShieldCheckIcon as ShieldCheck, PhoneIcon as Phone } from "@phosphor-icons/react";
import OrganizationSchema from "@/components/OrganizationSchema";

const BUSINESS_PHONE = process.env.REACT_APP_BUSINESS_PHONE || "";
const BUSINESS_PHONE_HREF = BUSINESS_PHONE.replace(/[^\d+]/g, "");

export default function Contact() {
  const [params] = useSearchParams();
  const initialSubject = params.get("subject") || "";
  const topic = (params.get("topic") || "general").toLowerCase();
  const [form, setForm] = useState({ name: "", email: "", subject: initialSubject, body: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const formRef = useRef(null);

  // When the visitor lands here from a legal-page deep link with a pre-filled
  // subject, scroll the form into view so they don't miss it under the
  // mailto cards.
  useEffect(() => {
    if (initialSubject && formRef.current) {
      formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [initialSubject]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.subject.trim() || !form.body.trim()) {
      toast.error("All fields are required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/public/contact", {
        name: form.name.trim(),
        email: form.email.trim(),
        subject: form.subject.trim(),
        body: form.body.trim(),
        topic,
      });
      setSent(true);
      toast.success("Message sent — we'll reply to your email");
      setForm({ name: "", email: "", subject: "", body: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <LegalLayout eyebrow="Contact" title="Contact us" lastUpdated="February 3, 2026">
      <OrganizationSchema />
      <p>
        Need to reach the Armstrong K9 Registry team? Pick the
        channel that matches your question, or use the form below for general
        enquiries.
      </p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 my-8 not-prose">
        {BUSINESS_PHONE && (
          <div
            className="bg-white border border-[#E5E5E5] rounded-xl p-5"
            data-testid="contact-card-phone"
          >
            <Phone size={22} weight="duotone" className="text-gold mb-2" />
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
              Phone
            </p>
            <a
              href={`tel:${BUSINESS_PHONE_HREF}`}
              className="text-sm font-mono text-ink"
            >
              {BUSINESS_PHONE}
            </a>
            <p className="text-[11px] text-[#6B6B6B] mt-2 leading-snug">
              Mon–Fri, business hours US Eastern. Leave a voicemail with your
              name + email outside hours.
            </p>
          </div>
        )}
        <div className="bg-white border border-[#E5E5E5] rounded-xl p-5">
          <EnvelopeSimple size={22} weight="duotone" className="text-gold mb-2" />
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">General</p>
          <a href="mailto:hello@armstrongk9registry.com" className="text-sm font-mono text-ink break-all">
            hello@armstrongk9registry.com
          </a>
          <div className="mt-2">
            <ContactFormLink topic="general" subject="General inquiry" label="use form →" />
          </div>
        </div>
        <div className="bg-white border border-[#E5E5E5] rounded-xl p-5">
          <ShieldCheck size={22} weight="duotone" className="text-gold mb-2" />
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">Security</p>
          <a href="mailto:security@armstrongk9registry.com" className="text-sm font-mono text-ink break-all">
            security@armstrongk9registry.com
          </a>
          <div className="mt-2">
            <ContactFormLink topic="security" subject="Security report" label="use form →" />
          </div>
        </div>
        <div className="bg-white border border-[#E5E5E5] rounded-xl p-5">
          <Buildings size={22} weight="duotone" className="text-gold mb-2" />
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">Legal &amp; Privacy</p>
          <a href="mailto:privacy@armstrongk9registry.com" className="text-sm font-mono text-ink break-all">
            privacy@armstrongk9registry.com
          </a>
          <div className="mt-2">
            <ContactFormLink topic="privacy" subject="Privacy / legal inquiry" label="use form →" />
          </div>
        </div>
      </div>

      <h2 ref={formRef}>Send us a message</h2>
      <p>
        The form below reaches our admin team's inbox directly. Please
        include a real email address so we can reply.
      </p>

      {sent ? (
        <div
          className="bg-[#FBF7EC] border border-[#E5D08A] rounded-xl p-6 not-prose"
          data-testid="contact-sent-banner"
        >
          <p className="font-semibold text-[#8A6A00]">Thanks — your message is in.</p>
          <p className="text-sm text-[#6B6B6B] mt-1">
            We'll reply to the email address you provided. Expect a response
            within 2 business days.
          </p>
          <Button
            variant="outline"
            onClick={() => setSent(false)}
            className="mt-4 rounded-full"
            data-testid="contact-send-another-btn"
          >
            Send another message
          </Button>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4 not-prose" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
                Your name *
              </Label>
              <Input
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                maxLength={120}
                className="mt-1 rounded-lg"
                data-testid="contact-name"
                required
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
                Email *
              </Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                className="mt-1 rounded-lg"
                data-testid="contact-email"
                required
              />
            </div>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
              Subject *
            </Label>
            <Input
              value={form.subject}
              onChange={(e) => set("subject", e.target.value)}
              maxLength={120}
              className="mt-1 rounded-lg"
              data-testid="contact-subject"
              required
            />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
              Message *
            </Label>
            <Textarea
              value={form.body}
              onChange={(e) => set("body", e.target.value)}
              rows={6}
              maxLength={4000}
              className="mt-1 rounded-lg"
              data-testid="contact-body"
              required
            />
            <p className="mt-1 text-[11px] text-[#6B6B6B] text-right">
              {form.body.length}/4000
            </p>
          </div>
          <Button
            type="submit"
            disabled={submitting}
            className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white h-11 px-6"
            data-testid="contact-submit"
          >
            {submitting ? "Sending…" : "Send message"}
          </Button>
        </form>
      )}
    </LegalLayout>
  );
}
